import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { assignmentsApi, learningPathsApi, orgApi, resultsApi } from '../../api/client';
import EmployeeTrainingDetail from './EmployeeTrainingDetail';

type Variant = 'analytics' | 'status' | 'quiz' | 'reports' | 'leaderboard' | 'certification';

const TITLES: Record<Variant, string> = {
  analytics: 'Training Analytics',
  status: 'Training Status Overview',
  quiz: 'Quiz Performance Analytics',
  reports: 'Training Reports',
  leaderboard: 'Training Leaderboard',
  certification: 'Certification Compliance',
};

const COLORS = ['#4caf50', '#ff9800', '#9e9e9e', '#f44336', '#1976d2', '#9c27b0'];

type TrainingStatus = 'on_track' | 'in_progress' | 'overdue' | 'not_started';

function statusChip(status: TrainingStatus) {
  if (status === 'on_track') return <Chip label="On Track" color="success" size="small" />;
  if (status === 'in_progress') return <Chip label="In Progress" color="warning" size="small" />;
  if (status === 'overdue') return <Chip label="Overdue" color="error" size="small" />;
  return <Chip label="Not Started" variant="outlined" size="small" />;
}

function formatActivity(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString();
}

function ChartBox({ title, empty, children }: { title: string; empty?: boolean; children: React.ReactElement }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {empty ? (
          <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
            No data for this chart yet
          </Typography>
        ) : (
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              {children}
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

const InsightsPage: React.FC<{ variant: Variant; storeId?: string | null }> = ({ variant, storeId }) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [paths, setPaths] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [e, a, r, p] = await Promise.all([
          orgApi.listEmployees(storeId ? { storeId } : undefined),
          assignmentsApi.list(),
          resultsApi.list(),
          learningPathsApi.listAssignments(),
        ]);
        if (cancelled) return;
        const ids = new Set((e as any[]).map((emp) => emp.id));
        const scopedEmployees = e as any[];
        const scopedAssignments = storeId ? (a as any[]).filter((item) => ids.has(item.employeeId)) : (a as any[]);
        const assignmentIds = new Set(scopedAssignments.map((item) => item.id));
        const scopedResults = storeId
          ? (r as any[]).filter((item) => assignmentIds.has(item.assignmentId) || ids.has(item.assignment?.employeeId))
          : (r as any[]);
        const scopedPaths = storeId ? (p as any[]).filter((item) => ids.has(item.employeeId)) : (p as any[]);
        setEmployees(scopedEmployees);
        setAssignments(scopedAssignments);
        setResults(scopedResults);
        setPaths(scopedPaths);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const model = useMemo(() => {
    const complete = assignments.filter((a) => a.isTrainingComplete || a.status === 'completed');
    const inProgress = assignments.filter((a) => !complete.includes(a));
    const scores = results.map((r) => Number(r.score || 0));
    const avg = scores.length ? Math.round(scores.reduce((s, n) => s + n, 0) / scores.length) : 0;
    const passed = results.filter((r) => r.passed).length;
    const passRate = results.length ? Math.round((passed / results.length) * 100) : 0;
    const completionRate = assignments.length ? Math.round((complete.length / assignments.length) * 100) : 0;

    const byEmployee = employees.map((emp) => {
      const mine = assignments.filter((a) => a.employeeId === emp.id);
      const done = mine.filter((a) => a.isTrainingComplete || a.status === 'completed').length;
      const empResults = results.filter((r) => r.assignment?.employeeId === emp.id || r.assignment?.employee?.id === emp.id);
      const empAvg = empResults.length
        ? Math.round(empResults.reduce((s, r) => s + Number(r.score || 0), 0) / empResults.length)
        : 0;
      const progress = mine.length ? Math.round((done / mine.length) * 100) : 0;
      const empPaths = paths.filter((p) => p.employeeId === emp.id);
      const pathDone = empPaths.filter((p) => p.status === 'completed').length;
      return {
        id: emp.id,
        name: emp.name || 'Employee',
        email: emp.email || '—',
        managerName: emp.manager?.name || '—',
        department: emp.department || 'Unassigned',
        total: mine.length,
        completed: done,
        inProgress: mine.length - done,
        notStarted: mine.filter((a) => a.status === 'assigned' && !a.isTrainingComplete).length,
        progress,
        score: empAvg,
        passed: empResults.filter((r) => r.passed).length,
        pathTotal: empPaths.length,
        pathCompleted: pathDone,
      };
    });

    const started = byEmployee.filter((e) => e.completed > 0 || (e.inProgress > 0 && e.progress > 0)).length;
    const completedAll = byEmployee.filter((e) => e.total > 0 && e.completed === e.total).length;
    const notStarted = byEmployee.filter((e) => e.total === 0 || e.completed === 0).length;
    const overdue = paths.filter((p) => p.dueDate && new Date(p.dueDate) < new Date() && p.status !== 'completed').length;

    const statusPie = [
      { name: 'Completed all', value: completedAll, color: '#4caf50' },
      { name: 'In progress', value: Math.max(byEmployee.filter((e) => e.total > 0 && e.completed < e.total && e.completed > 0).length, 0), color: '#ff9800' },
      { name: 'Not started', value: byEmployee.filter((e) => e.total > 0 && e.completed === 0).length, color: '#9e9e9e' },
      { name: 'Overdue', value: overdue, color: '#f44336' },
    ].filter((d) => d.value > 0);

    const completionPie = [
      { name: 'Completed', value: complete.length, color: '#4caf50' },
      { name: 'Assigned', value: inProgress.length, color: '#1976d2' },
    ].filter((d) => d.value > 0);

    const progressRanges = [
      { range: '0%', count: byEmployee.filter((e) => e.total > 0 && e.progress === 0).length },
      { range: '1-25%', count: byEmployee.filter((e) => e.progress > 0 && e.progress <= 25).length },
      { range: '26-50%', count: byEmployee.filter((e) => e.progress > 25 && e.progress <= 50).length },
      { range: '51-75%', count: byEmployee.filter((e) => e.progress > 50 && e.progress <= 75).length },
      { range: '76-99%', count: byEmployee.filter((e) => e.progress > 75 && e.progress < 100).length },
      { range: '100%', count: byEmployee.filter((e) => e.total > 0 && e.progress === 100).length },
    ];

    const deptMap = new Map<string, { department: string; completed: number; inProgress: number; notStarted: number; overdue: number; progressSum: number; people: number }>();
    byEmployee.forEach((e) => {
      const row = deptMap.get(e.department) || { department: e.department, completed: 0, inProgress: 0, notStarted: 0, overdue: 0, progressSum: 0, people: 0 };
      row.completed += e.completed;
      row.inProgress += Math.max(e.total - e.completed, 0);
      if (e.total > 0 && e.completed === 0) row.notStarted += 1;
      row.progressSum += e.progress;
      row.people += 1;
      deptMap.set(e.department, row);
    });
    const departmentData = Array.from(deptMap.values()).map((d) => ({
      ...d,
      avgProgress: d.people ? Math.round(d.progressSum / d.people) : 0,
    }));

    const courseMap = new Map<string, { title: string; total: number; completed: number; scores: number[]; passed: number; attempts: number }>();
    assignments.forEach((a) => {
      const title = a.course?.title || 'Course';
      const row = courseMap.get(a.courseId) || { title, total: 0, completed: 0, scores: [] as number[], passed: 0, attempts: 0 };
      row.total += 1;
      if (a.isTrainingComplete || a.status === 'completed') row.completed += 1;
      courseMap.set(a.courseId, row);
    });
    results.forEach((r) => {
      const courseId = r.assignment?.courseId;
      const title = r.assignment?.course?.title || 'Course';
      if (!courseId) return;
      const row = courseMap.get(courseId) || { title, total: 0, completed: 0, scores: [] as number[], passed: 0, attempts: 0 };
      row.scores.push(Number(r.score || 0));
      row.attempts += 1;
      if (r.passed) row.passed += 1;
      courseMap.set(courseId, row);
    });
    const courseStats = Array.from(courseMap.values()).map((c) => ({
      title: c.title,
      total: c.total,
      completed: c.completed,
      completionRate: c.total ? Math.round((c.completed / c.total) * 100) : 0,
      averageScore: c.scores.length ? Math.round(c.scores.reduce((s, n) => s + n, 0) / c.scores.length) : 0,
      passRate: c.attempts ? Math.round((c.passed / c.attempts) * 100) : 0,
    }));

    const scoreBuckets = [
      { range: '0-59', count: scores.filter((n) => n < 60).length },
      { range: '60-69', count: scores.filter((n) => n >= 60 && n < 70).length },
      { range: '70-79', count: scores.filter((n) => n >= 70 && n < 80).length },
      { range: '80-89', count: scores.filter((n) => n >= 80 && n < 90).length },
      { range: '90-100', count: scores.filter((n) => n >= 90).length },
    ];

    const passPie = [
      { name: 'Passed', value: passed, color: '#4caf50' },
      { name: 'Failed', value: results.length - passed, color: '#f44336' },
    ].filter((d) => d.value > 0);

    const leaderboard = [...byEmployee].filter((e) => e.total > 0 || e.score > 0).sort((a, b) => b.score - a.score || b.completed - a.completed);

    const recentCompletions = [...results]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 8)
      .map((r) => ({
        id: r.id,
        employeeName: r.assignment?.employee?.name || 'Employee',
        courseTitle: r.assignment?.course?.title || 'Course',
        completedAt: r.createdAt,
        score: Number(r.score || 0),
        passed: !!r.passed,
      }));

    const certPie = [
      { name: 'Valid', value: paths.filter((p) => p.certificationStatus === 'valid' || p.certificationStatus === 'active').length, color: '#4caf50' },
      { name: 'Expiring', value: paths.filter((p) => p.certificationStatus === 'expiring').length, color: '#ff9800' },
      { name: 'Expired', value: paths.filter((p) => p.certificationStatus === 'expired').length, color: '#f44336' },
      { name: 'In progress', value: paths.filter((p) => !p.certificationStatus || p.certificationStatus === 'pending').length, color: '#1976d2' },
    ].filter((d) => d.value > 0);

    return {
      complete: complete.length,
      avg,
      passed,
      passRate,
      completionRate,
      started,
      completedAll,
      notStarted,
      overdue,
      statusPie,
      completionPie,
      progressRanges,
      departmentData,
      courseStats,
      scoreBuckets,
      passPie,
      leaderboard,
      employeeProgress: byEmployee,
      recentCompletions,
      certPie,
    };
  }, [assignments, employees, paths, results]);

  const statusRows = useMemo(() => {
    const now = Date.now();
    const overdueAfter = 30 * 24 * 60 * 60 * 1000;
    return employees.map((emp) => {
      const courseAssignments = assignments.filter((a) => a.employeeId === emp.id && a.assignmentSource !== 'learning_path');
      const courseCompleted = courseAssignments.filter((a) => a.isTrainingComplete || a.status === 'completed');
      const courseOpen = courseAssignments.filter((a) => !(a.isTrainingComplete || a.status === 'completed'));
      const courseOverdue = courseOpen.some((a) => a.createdAt && now - new Date(a.createdAt).getTime() > overdueAfter);
      const empPaths = paths.filter((p) => p.employeeId === emp.id);
      const pathCompleted = empPaths.filter((p) => p.status === 'completed');
      const pathOpen = empPaths.filter((p) => p.status !== 'completed');
      const pathInProgress = pathOpen.filter((p) => p.status === 'in_progress');
      const pathNotStarted = pathOpen.length - pathInProgress.length;
      const pathOverdue = pathOpen.some((p) => p.dueDate && new Date(p.dueDate).getTime() < now);

      const courseStatus: TrainingStatus = courseAssignments.length === 0
        ? 'not_started'
        : courseCompleted.length === courseAssignments.length
          ? 'on_track'
          : courseOverdue
            ? 'overdue'
            : courseCompleted.length > 0 || courseOpen.length > 0
              ? 'in_progress'
              : 'not_started';
      const pathStatus: TrainingStatus = empPaths.length === 0
        ? 'not_started'
        : pathCompleted.length === empPaths.length
          ? 'on_track'
          : pathOverdue
            ? 'overdue'
            : pathCompleted.length > 0 || pathInProgress.length > 0
              ? 'in_progress'
              : 'not_started';

      const assigned = courseAssignments.length + empPaths.length;
      const completed = courseCompleted.length + pathCompleted.length;
      const overdue = courseOverdue || pathOverdue;
      const overall: TrainingStatus = assigned === 0
        ? 'not_started'
        : completed === assigned
          ? 'on_track'
          : overdue
            ? 'overdue'
            : completed > 0 || pathInProgress.length > 0 || courseOpen.length > 0
              ? 'in_progress'
              : 'not_started';

      const dates = [
        ...courseAssignments.flatMap((a) => [a.trainingCompletedAt, a.updatedAt]),
        ...empPaths.flatMap((p) => [p.completedDate, p.updatedAt]),
      ].filter(Boolean) as string[];
      const lastActivity = dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;

      return {
        id: emp.id,
        name: emp.name || 'Employee',
        email: emp.email || '',
        department: emp.department || '',
        courseAssigned: courseAssignments.length,
        courseCompleted: courseCompleted.length,
        courseInProgress: courseOpen.length,
        courseNotStarted: Math.max(courseAssignments.length - courseCompleted.length - courseOpen.length, 0),
        courseProgress: courseAssignments.length ? Math.round((courseCompleted.length / courseAssignments.length) * 100) : 0,
        courseStatus,
        pathAssigned: empPaths.length,
        pathCompleted: pathCompleted.length,
        pathInProgress: pathInProgress.length,
        pathNotStarted,
        pathProgress: empPaths.length ? Math.round((pathCompleted.length / empPaths.length) * 100) : 0,
        pathStatus,
        overall,
        lastActivity,
      };
    });
  }, [assignments, employees, paths]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((emp) => emp.department).filter(Boolean))).sort(),
    [employees],
  );

  const filteredStatusRows = statusRows.filter((row) => {
    const query = searchQuery.trim().toLowerCase();
    if (query && !row.name.toLowerCase().includes(query) && !row.email.toLowerCase().includes(query)) return false;
    if (departmentFilter !== 'all' && row.department !== departmentFilter) return false;
    if (statusFilter !== 'all' && row.overall !== statusFilter) return false;
    return true;
  });

  const selectedEmployee = employees.find((emp) => emp.id === selectedEmployeeId);
  if (variant === 'status' && selectedEmployee) {
    return (
      <EmployeeTrainingDetail
        employee={selectedEmployee}
        assignments={assignments.filter((item) => item.employeeId === selectedEmployee.id)}
        results={results.filter((item) => item.assignment?.employeeId === selectedEmployee.id || assignments.some((row) => row.id === item.assignmentId && row.employeeId === selectedEmployee.id))}
        pathAssignments={paths.filter((item) => item.employeeId === selectedEmployee.id)}
        onBack={() => setSelectedEmployeeId(null)}
      />
    );
  }

  const statCard = (label: string, value: string | number, color?: string) => (
    <Grid item xs={12} sm={6} md={3}>
      <Card>
        <CardContent>
          <Typography color="text.secondary" variant="body2">{label}</Typography>
          <Typography variant="h4" sx={{ color }}>{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{TITLES[variant]}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCard('Employees', employees.length)}
        {statCard('Assignments', assignments.length)}
        {statCard('Completed', model.complete, '#2e7d32')}
        {statCard(variant === 'quiz' ? 'Average score' : 'Completion', variant === 'quiz' ? `${model.avg}%` : `${model.completionRate}%`, '#1976d2')}
      </Grid>

      {(variant === 'status' || variant === 'analytics') && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <ChartBox title="Status Distribution" empty={model.statusPie.length === 0}>
              <PieChart>
                <Pie data={model.statusPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {model.statusPie.map((entry, i) => <Cell key={entry.name} fill={entry.color || COLORS[i]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Completion Overview" empty={model.completionPie.length === 0}>
              <PieChart>
                <Pie data={model.completionPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {model.completionPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Progress Distribution" empty={!model.progressRanges.some((r) => r.count > 0)}>
              <BarChart data={model.progressRanges}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2196f3" name="Employees" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={8}>
            <ChartBox title="Department Breakdown" empty={model.departmentData.length === 0}>
              <BarChart data={model.departmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#4caf50" name="Completed" />
                <Bar dataKey="inProgress" stackId="a" fill="#ff9800" name="In Progress" />
                <Bar dataKey="notStarted" stackId="a" fill="#9e9e9e" name="Not Started" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartBox title="Avg Progress by Department" empty={model.departmentData.length === 0}>
              <BarChart data={model.departmentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="department" type="category" width={110} />
                <Tooltip />
                <Bar dataKey="avgProgress" fill="#2196f3" name="Average progress %" />
              </BarChart>
            </ChartBox>
          </Grid>
        </Grid>
      )}

      {variant === 'status' && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select value={departmentFilter} label="Department" onChange={(e) => setDepartmentFilter(e.target.value)}>
                      <MenuItem value="all">All Departments</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="on_track">On Track</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="not_started">Not Started</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Course Assignments Status</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell align="right">Assigned</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">In Progress</TableCell>
                      <TableCell align="right">Not Started</TableCell>
                      <TableCell align="right">Progress %</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStatusRows.filter((row) => row.courseAssigned > 0).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>No employees with course assignments match these filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredStatusRows.filter((row) => row.courseAssigned > 0).map((row) => (
                      <TableRow key={row.id} hover onClick={() => setSelectedEmployeeId(row.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                        </TableCell>
                        <TableCell>{row.department || 'N/A'}</TableCell>
                        <TableCell align="right">{row.courseAssigned}</TableCell>
                        <TableCell align="right">{row.courseCompleted}</TableCell>
                        <TableCell align="right">{row.courseInProgress}</TableCell>
                        <TableCell align="right">{row.courseNotStarted}</TableCell>
                        <TableCell align="right">{row.courseProgress}%</TableCell>
                        <TableCell>{statusChip(row.courseStatus)}</TableCell>
                        <TableCell>{formatActivity(row.lastActivity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Learning Path Assignments Status</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell align="right">Assigned</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">In Progress</TableCell>
                      <TableCell align="right">Not Started</TableCell>
                      <TableCell align="right">Progress %</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStatusRows.filter((row) => row.pathAssigned > 0).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>No employees with learning path assignments match these filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredStatusRows.filter((row) => row.pathAssigned > 0).map((row) => (
                      <TableRow key={row.id} hover onClick={() => setSelectedEmployeeId(row.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                        </TableCell>
                        <TableCell>{row.department || 'N/A'}</TableCell>
                        <TableCell align="right">{row.pathAssigned}</TableCell>
                        <TableCell align="right">{row.pathCompleted}</TableCell>
                        <TableCell align="right">{row.pathInProgress}</TableCell>
                        <TableCell align="right">{row.pathNotStarted}</TableCell>
                        <TableCell align="right">{row.pathProgress}%</TableCell>
                        <TableCell>{statusChip(row.pathStatus)}</TableCell>
                        <TableCell>{formatActivity(row.lastActivity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {variant === 'analytics' && (
        <Box sx={{ display: 'grid', gap: '1rem', mb: 2 }}>
          <Typography variant="h6">Course Performance</Typography>
          {model.courseStats.length === 0 && <Typography color="text.secondary">No course assignments yet.</Typography>}
          {model.courseStats.map((stat) => (
            <Box key={stat.title} sx={{ bgcolor: 'white', p: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{stat.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.completed} / {stat.total} completed</Typography>
                </Box>
                <Typography variant="h5" sx={{ color: '#1976d2' }}>{stat.completionRate}%</Typography>
              </Box>
              <Box sx={{ width: '100%', height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                <Box sx={{ width: `${stat.completionRate}%`, height: '100%', bgcolor: '#1976d2' }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Avg Score: <strong>{stat.averageScore}%</strong> · Pass Rate: <strong>{stat.passRate}%</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {variant === 'analytics' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Employee Progress</Typography>
          {model.employeeProgress.length === 0 && (
            <Typography color="text.secondary">No employees yet.</Typography>
          )}
          {model.employeeProgress.length > 0 && (
          <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  {['Employee', 'Email', 'Manager', 'Courses', 'Learning Paths', 'Completion', 'Avg Score'].map((col) => (
                    <th key={col} style={{ padding: '0.85rem 1rem', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.employeeProgress.map((employee) => (
                  <tr key={employee.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '0.85rem 1rem' }}>{employee.name}</td>
                    <td style={{ padding: '0.85rem 1rem', color: '#666' }}>{employee.email}</td>
                    <td style={{ padding: '0.85rem 1rem', color: '#666' }}>{employee.managerName}</td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      {employee.total > 0 ? `${employee.completed} / ${employee.total}` : '—'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      {employee.pathTotal > 0 ? `${employee.pathCompleted} / ${employee.pathTotal}` : '—'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ width: `${employee.progress}%`, height: '100%', bgcolor: employee.progress >= 80 ? '#4caf50' : employee.progress >= 50 ? '#ff9800' : '#f44336' }} />
                        </Box>
                        <span style={{ minWidth: 42 }}>{employee.progress}%</span>
                      </Box>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: 700 }}>
                      {employee.score > 0 ? `${employee.score}%` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          )}
        </Box>
      )}

      {variant === 'analytics' && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Recent Completions</Typography>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', p: 2 }}>
            {model.recentCompletions.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No completions yet</Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {model.recentCompletions.map((completion) => (
                  <Box key={completion.id} sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{completion.employeeName} completed {completion.courseTitle}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {completion.completedAt ? new Date(completion.completedAt).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: completion.passed ? '#4caf50' : '#f44336' }}>{completion.score}%</Typography>
                      <Typography variant="body2" sx={{ color: completion.passed ? '#4caf50' : '#f44336' }}>
                        {completion.passed ? 'Passed' : 'Failed'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {variant === 'quiz' && (
        <Grid container spacing={2}>
          {statCard('Pass rate', `${model.passRate}%`, '#ed6c02')}
          {statCard('Quiz attempts', results.length)}
          <Grid item xs={12} md={4}>
            <ChartBox title="Pass / Fail" empty={model.passPie.length === 0}>
              <PieChart>
                <Pie data={model.passPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                  {model.passPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={8}>
            <ChartBox title="Score Distribution" empty={!model.scoreBuckets.some((b) => b.count > 0)}>
              <BarChart data={model.scoreBuckets}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" name="Attempts" />
              </BarChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12}>
            <ChartBox title="Average Score by Course" empty={model.courseStats.length === 0}>
              <BarChart data={model.courseStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="title" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="averageScore" fill="#4caf50" name="Average score" />
                <Bar dataKey="passRate" fill="#ff9800" name="Pass rate" />
              </BarChart>
            </ChartBox>
          </Grid>
        </Grid>
      )}

      {variant === 'reports' && (
        <ChartBox title="Completion by Course" empty={model.courseStats.length === 0}>
          <BarChart data={model.courseStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="title" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completed" fill="#4caf50" name="Completed" />
            <Bar dataKey="total" fill="#1976d2" name="Assigned" />
          </BarChart>
        </ChartBox>
      )}

      {variant === 'leaderboard' && (
        <ChartBox title="Scores by Employee" empty={model.leaderboard.length === 0}>
          <BarChart data={model.leaderboard.slice(0, 12)} layout="vertical" margin={{ left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} />
            <YAxis dataKey="name" type="category" width={120} />
            <Tooltip />
            <Bar dataKey="score" fill="#1976d2" name="Average score" />
          </BarChart>
        </ChartBox>
      )}

      {variant === 'certification' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <ChartBox title="Certification Status" empty={model.certPie.length === 0}>
              <PieChart>
                <Pie data={model.certPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                  {model.certPie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ChartBox>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'grid', gap: '1rem' }}>
              {paths.length === 0 && <Typography color="text.secondary">No certification assignments yet.</Typography>}
              {paths.map((a) => (
                <Box key={a.id} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: '1.5rem', bgcolor: 'white' }}>
                  <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{a.employee?.name} → {a.learningPath?.title}</Typography>
                  <Typography color="text.secondary">Status: {a.status || '—'} · Certification: {a.certificationStatus || '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default InsightsPage;
